import React, { useState } from "react";

interface Guideline {
  text: string;
  sc: string; // e.g. "1.4.1"
  title: string;
  href: string;
}

const GUIDELINES: Guideline[] = [
  {
    text: "Don't use color alone to convey meaning (e.g. red-only for errors).",
    sc: "1.4.1",
    title: "Use of Color",
    href: "https://www.w3.org/WAI/WCAG22/Understanding/use-of-color.html"
  },
  {
    text: "Don't use placeholder text as the only label for an input.",
    sc: "3.3.2",
    title: "Labels or Instructions",
    href: "https://www.w3.org/WAI/WCAG22/Understanding/labels-or-instructions.html"
  },
  {
    text: "Don't set header text contrast below 4.5:1 against the header background.",
    sc: "1.4.3",
    title: "Contrast (Minimum)",
    href: "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
  },
  {
    text: "Don't use font sizes below 14px for body text.",
    sc: "1.4.4",
    title: "Resize Text",
    href: "https://www.w3.org/WAI/WCAG22/Understanding/resize-text.html"
  },
  {
    text: "Don't set launcher size below 44 × 44 px — fails target size.",
    sc: "2.5.8",
    title: "Target Size (Minimum)",
    href: "https://www.w3.org/WAI/WCAG22/Understanding/target-size-minimum.html"
  },
  {
    text: "Don't disable keyboard navigation or hide focus rings.",
    sc: "2.4.7",
    title: "Focus Visible",
    href: "https://www.w3.org/WAI/WCAG22/Understanding/focus-visible.html"
  },
  {
    text: "Don't use autoplay sounds louder than ambient — notification sounds should be short and non-jarring.",
    sc: "1.4.2",
    title: "Audio Control",
    href: "https://www.w3.org/WAI/WCAG22/Understanding/audio-control.html"
  },
  {
    text: "Don't use animations longer than 5 seconds on a loop without a pause control.",
    sc: "2.2.2",
    title: "Pause, Stop, Hide",
    href: "https://www.w3.org/WAI/WCAG22/Understanding/pause-stop-hide.html"
  },
  {
    text: "Don't rely on hover-only tooltips — content must also be reachable via keyboard focus.",
    sc: "1.4.13",
    title: "Content on Hover or Focus",
    href: "https://www.w3.org/WAI/WCAG22/Understanding/content-on-hover-or-focus.html"
  },
  {
    text: "Don't set border-radius so high the widget shape becomes ambiguous for screen readers.",
    sc: "1.3.1",
    title: "Info and Relationships",
    href: "https://www.w3.org/WAI/WCAG22/Understanding/info-and-relationships.html"
  }
];

interface Props {
  wcagEnabled: boolean;
}

export default function WcagGuidancePanel({ wcagEnabled }: Props) {
  const [open, setOpen] = useState(false);
  const panelId = "wcag-guidance-panel";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg bg-white">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-slate-700 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
        style={{ minHeight: 40 }}
      >
        <span>Accessibility guidelines (WCAG 2.2 AA)</span>
        <span aria-hidden="true" className="text-slate-500">
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open && (
        <div id={panelId} className="px-4 pb-4 pt-1 text-sm text-slate-700">
          <div
            role="note"
            className="bg-amber-50 border border-amber-700 rounded-md p-3 mb-3 text-xs text-amber-900"
          >
            These are guidelines, not a guarantee of compliance. WCAG
            conformance depends on your final deployment, content, and host
            page. For legal compliance (ADA, EAA 2025, AODA, Section 508),
            consult an accessibility auditor. Velaro warns but does not block
            configurations that may fail accessibility checks.
          </div>
          {!wcagEnabled && (
            <div className="italic text-xs text-slate-500 mb-3">
              You have disabled WCAG warnings in Appearance settings. The
              guidelines below still apply.
            </div>
          )}
          <ul className="list-disc pl-5 space-y-1.5">
            {GUIDELINES.map((g) => (
              <li key={g.sc}>
                <span>{g.text} </span>
                <a
                  href={g.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--color-primary)] underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
                >
                  WCAG {g.sc} {g.title}
                  <span className="sr-only"> (opens in new tab)</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
